import { createRef, MutableRefObject, RefObject, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import AnchorTabs from '../anchor-tabs/AnchorTabs';

import { ContentBlock } from './ContentBlock';
import ContentBlockPlainElements from './ContentBlockPlainElements';
import { Section, AssetLinks } from './models';
import { createIdFromTitle, getSectionIdFromHash } from './utils';

import { renderRichText } from 'utils/rich-text-render';

interface Props {
  contentBlocks: Section[];
  assetLinks?: AssetLinks;
  disableAnchorTabs?: boolean;
  isFullWidth?: boolean;
}

export default function ContentBlocksWrapper({
  contentBlocks,
  assetLinks,
  disableAnchorTabs,
  isFullWidth = false,
}: Props) {
  const location = useLocation();
  const allSectionRefs: MutableRefObject<Dictionary<RefObject<HTMLDivElement>>> = useRef({});

  const plainRichTextContentBlocks = contentBlocks.filter((block) => !block.title);
  const richTextHeadingBlocks = contentBlocks.filter((block) => block.title);

  const filledInContentBlocks = richTextHeadingBlocks.map(({ title, document }) => {
    const id = createIdFromTitle(title);
    allSectionRefs.current[id] = createRef<HTMLDivElement>();

    return {
      ref: allSectionRefs.current[id],
      id,
      document,
      title,
    };
  });

  const links = filledInContentBlocks.map(({ id, title }) => ({ id, title }));

  const scrollToSection = (id: string) => {
    const section = allSectionRefs.current[id].current;
    section && scrollIntoView(section);
  };

  const getHashIdAndScroll = () => {
    const id = getSectionIdFromHash(location);
    const isExistingId = id && Object.hasOwn(allSectionRefs.current, id);

    isExistingId && scrollToSection(id);
  };

  useEffect(getHashIdAndScroll, [location]);

  return (
    <>
      <ContentBlockPlainElements
        sections={plainRichTextContentBlocks}
        assetLinks={assetLinks}
        isFullWidth={isFullWidth}
      />

      {!disableAnchorTabs && links.length > 1 && (
        <AnchorTabs links={links} onTabChange={(id) => scrollToSection(id)} />
      )}

      <div>
        {filledInContentBlocks.map(({ ref, id, title, document }) => (
          <ContentBlock
            ref={ref}
            id={id}
            key={id}
            title={title}
            onResize={getHashIdAndScroll}
            content={renderRichText(document, assetLinks)}
          />
        ))}
      </div>
    </>
  );
}
